import TimezoneUtils from './timezoneUtils';
import { resolveColorForName } from './nameColors';

export const buildDashboardDateFilter = ({ days, fromDate }) => ({
  days,
  fromDate: fromDate || '',
});

export const DASHBOARD_DATE_RANGE_OPTIONS = [
  { value: '7', label: 'Last 7 Days' },
  { value: '14', label: 'Last 14 Days' },
  { value: '30', label: 'Last 30 Days' },
  { value: '90', label: 'Last 3 Months' },
  { value: 'single', label: 'Single date' },
  { value: 'custom', label: 'Custom date' },
];

export const buildDashboardDateParams = ({ days, fromDate }) => {
  if (days === 'custom' || days === 'single') {
    return fromDate ? { fromDate } : {};
  }
  return { days };
};

export const formatDashboardRangeLabel = ({ days, fromDate }, presetOptions = DASHBOARD_DATE_RANGE_OPTIONS) => {
  if ((days === 'custom' || days === 'single') && !fromDate) {
    return days === 'single' ? 'Single date' : 'Custom date';
  }

  if (days === 'single' && fromDate) {
    const parsed = new Date(`${fromDate}T00:00:00.000Z`);
    if (!Number.isNaN(parsed.getTime())) {
      return parsed.toLocaleDateString();
    }
  }

  if (days === 'custom' && fromDate) {
    const parsed = new Date(`${fromDate}T00:00:00.000Z`);
    if (!Number.isNaN(parsed.getTime())) {
      return `From ${parsed.toLocaleDateString()} to today`;
    }
  }

  return presetOptions.find((option) => option.value === days)?.label || `Last ${days} Days`;
};

export const resolveDashboardChartDateRange = ({ days = '14', fromDate } = {}) => {
  const today = TimezoneUtils.now();

  if (days === 'single' && fromDate) {
    const parsed = new Date(`${fromDate}T00:00:00.000Z`);
    if (!Number.isNaN(parsed.getTime())) {
      const day = TimezoneUtils.startOfDayUTC(parsed);
      return {
        startDate: day,
        endDate: TimezoneUtils.endOfDayUTC(day),
      };
    }
  }

  const endDate = TimezoneUtils.endOfDayUTC(today);
  let startDate;

  if (fromDate) {
    const parsed = new Date(`${fromDate}T00:00:00.000Z`);
    startDate = Number.isNaN(parsed.getTime())
      ? TimezoneUtils.addDaysUTC(TimezoneUtils.startOfDayUTC(today), -14)
      : TimezoneUtils.startOfDayUTC(parsed);
  } else {
    const daysNum = parseInt(days, 10);
    const safeDays = Number.isNaN(daysNum) ? 14 : daysNum;
    startDate = TimezoneUtils.addDaysUTC(TimezoneUtils.startOfDayUTC(today), -safeDays);
  }

  return {
    startDate: TimezoneUtils.startOfDayUTC(startDate),
    endDate,
  };
};

export const COST_CHART_SERIES = [
  {
    key: 'totalUsd',
    label: 'Total',
    borderColor: '#1565c0',
    backgroundColor: 'rgba(21, 101, 192, 0.12)',
    fill: true,
  },
  {
    key: 'jobSearchOpenaiUsd',
    label: 'Job Search OpenAI',
    borderColor: '#ed6c02',
    backgroundColor: 'rgba(237, 108, 2, 0.12)',
  },
  {
    key: 'jobSearchScrapingdogUsd',
    label: 'Job Search ScrapingDog',
    borderColor: '#6d4c41',
    backgroundColor: 'rgba(109, 76, 65, 0.12)',
  },
  {
    key: 'analyzeFormFieldsUsd',
    label: 'analyzeFormFields',
    borderColor: '#0288d1',
    backgroundColor: 'rgba(2, 136, 209, 0.12)',
  },
  {
    key: 'customizeResumeUsd',
    label: 'Customize Resume',
    borderColor: '#2e7d32',
    backgroundColor: 'rgba(46, 125, 50, 0.12)',
  },
  {
    key: 'extractionsUsd',
    label: 'All Extractions',
    borderColor: '#00897b',
    backgroundColor: 'rgba(0, 137, 123, 0.12)',
  },
  {
    key: 'othersUsd',
    label: 'Others',
    borderColor: '#546e7a',
    backgroundColor: 'rgba(84, 110, 122, 0.12)',
  },
];

const emptyChartTotals = () => ({
  totalUsd: 0,
  jobSearchOpenaiUsd: 0,
  jobSearchScrapingdogUsd: 0,
  analyzeFormFieldsUsd: 0,
  customizeResumeUsd: 0,
  extractionsUsd: 0,
  othersUsd: 0,
});

export const fillDailyCostSeries = (dailyEntries, dateRange, fromDate = '') => {
  const { startDate, endDate } = resolveDashboardChartDateRange({ days: dateRange, fromDate });
  const allDates = TimezoneUtils.generateDateRange(startDate, endDate);
  const dailyMap = {};

  (dailyEntries || []).forEach((entry) => {
    dailyMap[entry._id] = entry;
  });

  return allDates.map((dateKey) => {
    const entry = dailyMap[dateKey] || {};
    const jobSearchOpenaiUsd = entry.jobSearchOpenaiUsd || 0;
    const jobSearchScrapingdogUsd = entry.jobSearchScrapingdogUsd || 0;
    const analyzeFormFieldsUsd = entry.analyzeFormFieldsUsd || 0;
    const customizeResumeUsd = entry.customizeResumeUsd || 0;
    const extractionsUsd = entry.extractionsUsd || 0;
    const othersUsd = entry.othersUsd || 0;
    const bucketTotal = (
      jobSearchOpenaiUsd
      + jobSearchScrapingdogUsd
      + analyzeFormFieldsUsd
      + customizeResumeUsd
      + extractionsUsd
      + othersUsd
    );

    return {
      _id: dateKey,
      totalUsd: entry.totalUsd != null ? entry.totalUsd : bucketTotal,
      jobSearchOpenaiUsd,
      jobSearchScrapingdogUsd,
      analyzeFormFieldsUsd,
      customizeResumeUsd,
      extractionsUsd,
      othersUsd,
    };
  });
};

export const sumDailyCostSeries = (dailySeries) => (
  (dailySeries || []).reduce(
    (acc, entry) => ({
      totalUsd: acc.totalUsd + (entry.totalUsd || 0),
      jobSearchOpenaiUsd: acc.jobSearchOpenaiUsd + (entry.jobSearchOpenaiUsd || 0),
      jobSearchScrapingdogUsd: acc.jobSearchScrapingdogUsd + (entry.jobSearchScrapingdogUsd || 0),
      analyzeFormFieldsUsd: acc.analyzeFormFieldsUsd + (entry.analyzeFormFieldsUsd || 0),
      customizeResumeUsd: acc.customizeResumeUsd + (entry.customizeResumeUsd || 0),
      extractionsUsd: acc.extractionsUsd + (entry.extractionsUsd || 0),
      othersUsd: acc.othersUsd + (entry.othersUsd || 0),
    }),
    emptyChartTotals(),
  )
);

export const transformDailyCountsForChart = (dailyCounts, dateFilter) => {
  const { days = '14', fromDate } = typeof dateFilter === 'string'
    ? { days: dateFilter, fromDate: '' }
    : dateFilter;

  const { startDate, endDate } = resolveDashboardChartDateRange({ days, fromDate });
  const allDates = TimezoneUtils.generateDateRange(startDate, endDate);

  const countMap = {};
  (dailyCounts || []).forEach((entry) => {
    const key = entry.date || entry._id;
    if (key) {
      countMap[key] = entry.count;
    }
  });

  return allDates.map((date) => ({
    date: TimezoneUtils.formatDateWithDayOfWeek(date).join(' '),
    dateKey: date,
    count: countMap[date] || 0,
  }));
};

export const transformHistoryDataForChart = (rawData, dateFilter, profileColors, isDarkMode) => {
  if (!rawData?.dates?.length) {
    return { labels: [], datasets: [] };
  }

  const { days = '14', fromDate } = typeof dateFilter === 'string'
    ? { days: dateFilter, fromDate: '' }
    : dateFilter;

  const { startDate, endDate } = resolveDashboardChartDateRange({ days, fromDate });
  const allDates = TimezoneUtils.generateDateRange(startDate, endDate);

  const dataMap = {};
  rawData.dates.forEach((entry) => {
    dataMap[entry.date] = entry;
  });

  const profiles = rawData.profiles || [];
  const labels = allDates.map((date) => TimezoneUtils.formatDateWithDayOfWeek(date));
  const datasets = profiles.map((profile) => {
    const color = resolveColorForName(profile, profileColors, isDarkMode);
    return {
      label: profile,
      data: allDates.map((date) => {
        const entry = dataMap[date];
        return entry ? entry[profile] || 0 : 0;
      }),
      backgroundColor: color,
      borderColor: color,
      borderWidth: 1,
    };
  });

  return { labels, datasets };
};
